import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { getAdoptionOverview } from '../../lib/api';
import { UsersFour, Lightning, ChatTeardropText, Star, Warning, TrendUp, TrendDown, Info, DownloadSimple, UserPlus, DotsThreeVertical, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RANGES = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const STATUS_STYLES = {
  'Power User': 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  'Active': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  'Developing': 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  'At Risk': 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  'Inactive': 'bg-red-500/15 text-red-500 border-red-500/30',
  'Not Activated': 'bg-muted text-muted-foreground border-border',
};

function ScoreRing({ score, size = 40 }) {
  const stroke = 3.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(100, Math.max(0, score));
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : pct > 0 ? '#ef4444' : '#525252';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.4s ease' }} />
      </svg>
      <span className="absolute text-xs font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function TrendChip({ pct, hasPrev }) {
  if (!hasPrev && pct === 0) return <span className="text-xs text-muted-foreground">—</span>;
  if (pct === 0) return <span className="text-xs text-muted-foreground">0%</span>;
  const positive = pct > 0;
  const Icon = positive ? TrendUp : TrendDown;
  const color = positive ? 'text-emerald-500' : 'text-red-500';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      <Icon size={12} weight="bold" />{positive ? '+' : ''}{pct}%
    </span>
  );
}

function KpiCard({ icon: Icon, iconColor, label, value, sub, trend, trendLabel, testId }) {
  return (
    <Card className="bg-card border-border" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${iconColor}18`, border: `1px solid ${iconColor}30` }}>
            <Icon size={18} weight="fill" style={{ color: iconColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-muted-foreground font-medium">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-0.5 leading-tight">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
            {trend !== undefined && trend !== null && (
              <div className="mt-1 flex items-center gap-1">
                <TrendChip pct={trend} hasPrev />
                {trendLabel && <span className="text-[10px] text-muted-foreground">{trendLabel}</span>}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreExplain({ row }) {
  const r = row.score_reasons || {};
  return (
    <div className="space-y-2 max-w-[260px]">
      <p className="text-xs font-bold text-foreground">Why {row.name} is {row.adoption_score}</p>
      <div className="space-y-1 text-[11px]">
        <div className="flex justify-between gap-4"><span className="text-muted-foreground">Frequency (30d)</span><span className="text-foreground font-semibold">{r.frequency}/30</span></div>
        <div className="flex justify-between gap-4"><span className="text-muted-foreground">Recency</span><span className="text-foreground font-semibold">{r.recency}/25</span></div>
        <div className="flex justify-between gap-4"><span className="text-muted-foreground">Consistency</span><span className="text-foreground font-semibold">{r.consistency}/25</span></div>
        <div className="flex justify-between gap-4"><span className="text-muted-foreground">Feature depth</span><span className="text-foreground font-semibold">{r.feature_depth}/20</span></div>
      </div>
      <div className="pt-2 border-t border-border/50 text-[11px] text-muted-foreground space-y-0.5">
        <p>Active {r.active_days_last_7} of last 7 days</p>
        <p>{r.sessions_last_30_days} sessions in last 30 days</p>
        <p>{r.distinct_features} distinct Clara features used</p>
      </div>
    </div>
  );
}

function formatLastUsed(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return `Today, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function actionForStatus(status) {
  if (status === 'Not Activated') return { primary: 'Send Invite', secondary: 'Send Quick Start' };
  if (status === 'At Risk' || status === 'Inactive') return { primary: 'Coach User', secondary: 'View Activity' };
  return { primary: 'View Activity', secondary: 'View Usage' };
}

export default function AdoptionOverview({ onOpenTeamAccess, onOpenActivityFeed, onOpenFeatureUsage, onOpenUserDetail }) {
  const [range, setRange] = useState('this_week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getAdoptionOverview(range)); }
    catch (e) { toast.error('Failed to load adoption data'); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.rows) return;
    const header = ['Team Member', 'Role', 'Adoption Score', 'This Week', 'Last 30 Days', 'Last Used', 'Primary Use', 'Trend %', 'Status'];
    const rows = data.rows.map(r => [
      r.name, r.role, r.adoption_score, r.sessions_this_week, r.sessions_last_30_days,
      r.last_used ? new Date(r.last_used).toLocaleString() : 'Never', r.primary_use, r.trend_pct, r.status,
    ]);
    const csv = [header, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `clara-adoption-${range}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success('Exported CSV');
  };

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const { kpis, rows, feature_usage, intelligence } = data;
  const visibleRows = showAll ? rows : rows.slice(0, 8);
  const donutTotal = feature_usage.reduce((s, f) => s + f.count, 0) || 1;
  const featureColors = ['#10b981', '#3b82f6', '#f59e0b', '#a855f7', '#ec4899', '#525252', '#0ea5e9', '#f43f5e'];

  return (
    <TooltipProvider>
    <div className="space-y-5" data-testid="adoption-overview">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Adoption Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Track how your team is using Clara and driving results.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40 h-9 border-border text-xs" data-testid="date-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} className="h-9 border-border text-xs" data-testid="export-btn">
            <DownloadSimple size={14} className="mr-1.5" /> Export
          </Button>
          <Button onClick={onOpenTeamAccess} className="h-9 bg-[#E63935] hover:bg-[#d32f2f] text-white text-xs" data-testid="add-member-shortcut">
            <UserPlus size={14} className="mr-1.5" /> Add Team Member
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-row">
        <KpiCard icon={UsersFour} iconColor="#10b981" label="Team Adoption"
          value={`${kpis.team_adoption_pct}%`}
          sub={`${kpis.team_adoption_active} of ${kpis.team_adoption_total} active`}
          trend={kpis.team_adoption_trend} trendLabel="vs last period" testId="kpi-adoption" />
        <KpiCard icon={Lightning} iconColor="#3b82f6" label="Active This Week"
          value={`${kpis.active_this_week} of ${kpis.active_this_week_total}`}
          sub="Team members" trend={kpis.active_this_week_trend} trendLabel="vs last week" testId="kpi-active" />
        <KpiCard icon={ChatTeardropText} iconColor="#a855f7" label="Total Uses"
          value={kpis.total_uses} sub="All team members"
          trend={kpis.total_uses_trend} trendLabel="vs last period" testId="kpi-total-uses" />
        <KpiCard icon={Star} iconColor="#f59e0b" label="Heavy Users"
          value={kpis.heavy_users} sub="Using Clara consistently"
          testId="kpi-heavy" />
        <KpiCard icon={Warning} iconColor="#ef4444" label="Needs Attention"
          value={kpis.needs_attention} sub="Low or no recent activity"
          testId="kpi-needs-attention" />
      </div>

      {/* Team Adoption Table */}
      <Card className="bg-card border-border" data-testid="adoption-table">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border">
            <h2 className="text-base font-bold text-foreground">Team Adoption &amp; Usage</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Team Member</th>
                  <th className="text-left px-3 py-2.5 font-medium">
                    <span className="inline-flex items-center gap-1">Adoption Score
                      <Tooltip><TooltipTrigger><Info size={11} /></TooltipTrigger>
                        <TooltipContent className="max-w-[280px] text-xs">Composite of frequency (30d), recency, consistency (active days last 7), and feature depth. Hover any score for full breakdown.</TooltipContent>
                      </Tooltip>
                    </span>
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium">This Week</th>
                  <th className="text-left px-3 py-2.5 font-medium">Last 30d</th>
                  <th className="text-left px-3 py-2.5 font-medium">Last Used</th>
                  <th className="text-left px-3 py-2.5 font-medium">Primary Use</th>
                  <th className="text-left px-3 py-2.5 font-medium">Trend</th>
                  <th className="text-left px-3 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r => {
                  const actions = actionForStatus(r.status);
                  return (
                    <tr key={r.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors group" data-testid={`row-${r.initials.toLowerCase()}`}>
                      <td className="px-4 py-2.5">
                        <button onClick={() => onOpenUserDetail && onOpenUserDetail(r)} className="flex items-center gap-2.5 text-left group/name">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold ${r.revoked ? 'bg-red-500/20 text-red-500' : 'bg-[#E63935]/20 text-[#E63935]'}`}>{r.initials}</span>
                          <div>
                            <p className="text-sm font-semibold text-foreground group-hover/name:text-[#E63935] transition-colors">{r.name}</p>
                            <p className="text-[10px] text-muted-foreground">{r.role || '—'}</p>
                          </div>
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <Tooltip>
                          <TooltipTrigger className="cursor-help"><ScoreRing score={r.adoption_score} /></TooltipTrigger>
                          <TooltipContent side="right"><ScoreExplain row={r} /></TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-foreground font-semibold">{r.sessions_this_week}</td>
                      <td className="px-3 py-2.5 text-sm text-foreground">{r.sessions_last_30_days}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{formatLastUsed(r.last_used)}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs px-2 py-1 rounded-md bg-muted/50 text-muted-foreground">{r.primary_use}</span>
                      </td>
                      <td className="px-3 py-2.5"><TrendChip pct={r.trend_pct} hasPrev={r.prev_period_sessions > 0 || r.sessions_period > 0} /></td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLES[r.status] || STATUS_STYLES['Developing']}`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => onOpenUserDetail && onOpenUserDetail(r)}
                            className={`h-7 text-[11px] px-2 border-border ${r.status === 'At Risk' || r.status === 'Inactive' ? 'text-amber-500 border-amber-500/30' : ''} ${r.status === 'Not Activated' ? 'text-[#E63935] border-[#E63935]/30' : ''}`}
                            data-testid={`action-${r.initials.toLowerCase()}`}>
                            {actions.primary} <ArrowRight size={10} className="ml-1" />
                          </Button>
                          <button className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground" data-testid={`row-menu-${r.initials.toLowerCase()}`}>
                            <DotsThreeVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > 8 && (
            <div className="border-t border-border p-2 text-center">
              <button onClick={() => setShowAll(!showAll)} className="text-xs text-muted-foreground hover:text-foreground">
                {showAll ? 'Show less' : `Show all ${rows.length} members`}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Intelligence + Recommended Actions + Feature Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-card border-border lg:col-span-1" data-testid="adoption-intelligence">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-base font-bold text-foreground">Adoption Intelligence</h3>
              <Tooltip><TooltipTrigger><Info size={12} className="text-muted-foreground" /></TooltipTrigger>
                <TooltipContent className="text-xs max-w-[220px]">Insights generated from real usage data. No fabricated activity.</TooltipContent></Tooltip>
            </div>
            {intelligence.length === 0 ? (
              <p className="text-xs text-muted-foreground">Not enough activity yet to surface insights. Encourage the team to try Clara.</p>
            ) : (
              <div className="space-y-3">
                {intelligence.map((ins, i) => {
                  const styles = {
                    positive: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-500', icon: '✓' },
                    warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-500', icon: '!' },
                    danger: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-500', icon: '✕' },
                    momentum: { bg: 'bg-sky-500/10', border: 'border-sky-500/20', text: 'text-sky-500', icon: '↑' },
                  }[ins.type] || { bg: 'bg-muted/30', border: 'border-border', text: 'text-muted-foreground', icon: '·' };
                  return (
                    <div key={i} className={`rounded-lg border ${styles.border} ${styles.bg} p-2.5`}>
                      <div className="flex items-start gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${styles.text}`}>{styles.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold ${styles.text}`}>{ins.title}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{ins.detail}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="recommended-actions">
          <CardContent className="p-4">
            <h3 className="text-base font-bold text-foreground mb-3">Recommended Actions</h3>
            <div className="space-y-2">
              <button onClick={onOpenActivityFeed} className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background/50 hover:border-[#E63935]/40 hover:bg-[#E63935]/5 p-2.5 text-left transition-colors">
                <div>
                  <p className="text-xs font-semibold text-foreground">Coach At-Risk Users</p>
                  <p className="text-[10px] text-muted-foreground">{kpis.needs_attention} users</p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground" />
              </button>
              <button onClick={onOpenTeamAccess} className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background/50 hover:border-[#E63935]/40 hover:bg-[#E63935]/5 p-2.5 text-left transition-colors">
                <div>
                  <p className="text-xs font-semibold text-foreground">Send Quick Start Guide</p>
                  <p className="text-[10px] text-muted-foreground">{kpis.not_activated} not activated</p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground" />
              </button>
              <button onClick={onOpenActivityFeed} className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background/50 hover:border-[#E63935]/40 hover:bg-[#E63935]/5 p-2.5 text-left transition-colors">
                <div>
                  <p className="text-xs font-semibold text-foreground">View Team Activity Feed</p>
                  <p className="text-[10px] text-muted-foreground">Real-time usage log</p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground" />
              </button>
              <button onClick={onOpenFeatureUsage} className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background/50 hover:border-[#E63935]/40 hover:bg-[#E63935]/5 p-2.5 text-left transition-colors">
                <div>
                  <p className="text-xs font-semibold text-foreground">See What They&apos;re Using</p>
                  <p className="text-[10px] text-muted-foreground">Feature-level breakdown</p>
                </div>
                <ArrowRight size={14} className="text-muted-foreground" />
              </button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="feature-usage-panel">
          <CardContent className="p-4">
            <h3 className="text-base font-bold text-foreground mb-3">What Are They Using Clara For?</h3>
            {feature_usage.length === 0 ? (
              <p className="text-xs text-muted-foreground">No usage in this period yet.</p>
            ) : (
              <div className="flex items-center gap-4">
                <svg width="110" height="110" viewBox="0 0 42 42" className="flex-shrink-0">
                  {(() => {
                    let offset = 25;
                    return feature_usage.map((f, i) => {
                      const dash = (f.count / donutTotal) * 100;
                      const el = (
                        <circle key={f.key} cx="21" cy="21" r="15.9155" fill="transparent"
                          stroke={featureColors[i % featureColors.length]} strokeWidth="4"
                          strokeDasharray={`${dash} ${100 - dash}`} strokeDashoffset={offset} />
                      );
                      offset = offset - dash;
                      return el;
                    });
                  })()}
                  <text x="21" y="20" textAnchor="middle" fontSize="6" fontWeight="700" fill="currentColor">{kpis.total_uses}</text>
                  <text x="21" y="26" textAnchor="middle" fontSize="3" fill="currentColor" opacity="0.6">Total Uses</text>
                </svg>
                <div className="flex-1 space-y-1.5">
                  {feature_usage.slice(0, 6).map((f, i) => (
                    <button key={f.key} onClick={onOpenFeatureUsage}
                      className="w-full flex items-center justify-between gap-2 hover:bg-muted/30 rounded px-1.5 py-1 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: featureColors[i % featureColors.length] }} />
                        <span className="text-xs text-foreground truncate">{f.label}</span>
                      </div>
                      <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">{f.pct}%</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button onClick={onOpenFeatureUsage} className="text-xs text-[#E63935] hover:underline mt-3 inline-flex items-center gap-1">
              View Full Feature Usage Report <ArrowRight size={11} />
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
    </TooltipProvider>
  );
}
